// Co-located simulate query (read-only) alongside rule-engine mutations.
import { graphql } from "gql.tada";
import {
  PresetScriptFieldsFragment,
  ScriptErrorFieldsFragment,
  ScriptWarningFieldsFragment,
  StepRuleSetFieldsFragment,
} from "./fragments";

export const CREATE_STEP_RULE_SET_MUTATION = graphql(
  `
    mutation CreateStepRuleSet(
      $tournamentStepId: ID!
      $input: CreateStepRuleSetInput!
    ) {
      createStepRuleSet(tournamentStepId: $tournamentStepId, input: $input) {
        ...StepRuleSetFields
      }
    }
  `,
  [StepRuleSetFieldsFragment],
);

export const APPLY_PRESET_SCRIPT_MUTATION = graphql(
  `
    mutation ApplyPresetScript(
      $presetScriptId: ID!
      $tournamentStepId: ID!
      $parameters: String
    ) {
      applyPresetScript(
        presetScriptId: $presetScriptId
        tournamentStepId: $tournamentStepId
        parameters: $parameters
      ) {
        ...StepRuleSetFields
      }
    }
  `,
  [StepRuleSetFieldsFragment],
);

export const APPLY_BUILTIN_PRESET_MUTATION = graphql(`
  mutation ApplyBuiltinPreset(
    $builtinPresetId: String!
    $tournamentStepId: ID!
    $parameters: String
  ) {
    applyBuiltinPreset(
      builtinPresetId: $builtinPresetId
      tournamentStepId: $tournamentStepId
      parameters: $parameters
    ) {
      id
      version
    }
  }
`);

export const DUPLICATE_BUILTIN_PRESET_MUTATION = graphql(`
  mutation DuplicateBuiltinPreset(
    $builtinPresetId: String!
    $name: String
  ) {
    duplicateBuiltinPreset(builtinPresetId: $builtinPresetId, name: $name) {
      id
      name
    }
  }
`);

export const CREATE_PRESET_SCRIPT_MUTATION = graphql(
  `
    mutation CreatePresetScript($input: CreatePresetScriptInput!) {
      createPresetScript(input: $input) {
        ...PresetScriptFields
      }
    }
  `,
  [PresetScriptFieldsFragment],
);

export const UPDATE_PRESET_SCRIPT_MUTATION = graphql(
  `
    mutation UpdatePresetScript($id: ID!, $input: UpdatePresetScriptInput!) {
      updatePresetScript(id: $id, input: $input) {
        ...PresetScriptFields
      }
    }
  `,
  [PresetScriptFieldsFragment],
);

export const DELETE_PRESET_SCRIPT_MUTATION = graphql(`
  mutation DeletePresetScript($id: ID!) {
    deletePresetScript(id: $id)
  }
`);

export const VALIDATE_SCRIPT_MUTATION = graphql(
  `
    mutation ValidateScript($input: ValidateScriptInput!) {
      validateScript(input: $input) {
        valid
        errors {
          ...ScriptErrorFields
        }
        warnings {
          ...ScriptWarningFields
        }
      }
    }
  `,
  [ScriptErrorFieldsFragment, ScriptWarningFieldsFragment],
);

// `SimulateScriptInput` carries the new `overrides: SimulationContextOverridesInput`
// (preferred) and the deprecated `parameters: String` JSON blob. Console submits
// `overrides` only; `parameters` is intentionally absent from the operation
// signature to enforce the migration. When both are sent, backend lets
// `overrides` win — but we never send both from here.
export const SIMULATE_SCRIPT_QUERY = graphql(
  `
    query SimulateScript($input: SimulateScriptInput!) {
      simulateScript(input: $input) {
        success
        errors {
          ...ScriptErrorFields
        }
        warnings {
          ...ScriptWarningFields
        }
        effectsSummary {
          type
          description
        }
        partialEffectsSummary {
          type
          description
        }
        executionTimeMs
        instructionsUsed
      }
    }
  `,
  [ScriptErrorFieldsFragment, ScriptWarningFieldsFragment],
);
